import React, { useCallback, useEffect, useState } from 'react';
import { showNotification } from 'services/api/notifications';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { withSearchResults } from 'ui/components/Page/WithSearchResults';
import {
  fetchSearch,
  getSearches,
  transformDisplayPick,
  DisplayPick,
} from 'services/picking';
import {
  getOrderPriorities,
  fetchOrderPriorities,
} from 'services/settings/orderPriorities';
import {
  clearModuleNavigation,
  ModuleNavigationType,
  removeModuleNavigation,
} from 'services/moduleNavigation';
import { fetchCustomers, getCustomers } from 'services/customers';
import { fetchLocations, getLocations } from 'services/locations';
import { fetchUoms } from 'services/uoms';
import { Pagination } from 'services/search';
import { fetchUsers, getActiveUser, getUsers } from 'services/user';
import {
  fetchUsers as fetchUsersV2,
  getActiveUser as getActiveUserV2,
  getUsers as getUsersV2,
} from 'services/userV2';
import { fetchTags } from 'services/tags';
import { fetchSettingsPicking } from 'services/settings/picking/redux';
import { useUrlQueryObject } from 'services/url';
import { Dates } from 'helpers';
import { usePickingBulkActions } from './hooks/usePickingBulkActions';
import { usePickingPageData } from './hooks/usePickingPageData';

import { Routes } from '../../navigation';
import { PickingPageCmp, PickingPageProps } from './types';
import { PICKING_COLUMNS } from './components';
import { initialPagination } from './consts';
import { printPickTicketsReport } from './helpers';
import { PickingPageAction } from './types';
import { ModuleNavigation } from 'app/components/AppBar/components';
import { useFlags } from 'helpers/useFlags';
import { useLocation, useNavigate } from 'react-router-dom';
import PickingPageView from './PickingPage.view';

const PickingPage: PickingPageCmp = (props: PickingPageProps) => {
  const {
    searchState: searchResult,
    refreshSearchState: fetchSearchResult,
    isLoadingSearchState: isLoadingSearchResult,
    activeItemId: activePickId,
  } = props;

  const dispatch = useDispatch();

  const flags = useFlags();
  const globalStateUsers = useSelector(getUsers);
  const globalStateUsersV2 = useSelector(getUsersV2);
  const users = flags.newUserEndpoint ? globalStateUsersV2 : globalStateUsers;

  const { items: locations } = useSelector(getLocations);
  const { items: customers } = useSelector(getCustomers);
  const { items: orderPriorities } = useSelector(getOrderPriorities);

  const globalStateActiveUser = useSelector(getActiveUser);
  const globalStateActiveUserV2 = useSelector(getActiveUserV2);
  const { user: activeUser } = flags.newUserEndpoint
    ? globalStateActiveUserV2
    : globalStateActiveUser;

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [activeDate, setActiveDate] = useState<Dates>(Dates.DateCreated);

  const location = useLocation();
  const navigate = useNavigate();
  const [, extendUrlQuery] = useUrlQueryObject(navigate, location);

  const activeUserId = activeUser ? activeUser.id! : 0;

  const { searchItem, searchTags, reportPresets } = usePickingPageData({
    advancedSearchColumns: searchResult.advancedSearch.columns,
    activeUserId,
  });

  const handleBulkActionSuccess = useCallback(async () => {
    await fetchSearchResult();
    setSelectedItems([]);
  }, [fetchSearchResult, setSelectedItems]);

  const { executePickingBulkAction, isVoiding, isGrouping } =
    usePickingBulkActions({
      selectedPickIds: selectedItems,
      onActionSuccess: handleBulkActionSuccess,
    });

  useEffect(() => {
    dispatch(clearModuleNavigation(ModuleNavigationType.Sales));

    return () => {
      dispatch(removeModuleNavigation());
    };
  }, [dispatch]);

  const handlePickClicked = useCallback(
    (pickId: number) => {
      extendUrlQuery({ activeId: pickId });
    },
    [extendUrlQuery]
  );

  const handleDetailsCardClosed = useCallback(() => {
    extendUrlQuery({ activeId: null });
  }, [extendUrlQuery]);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult({ pagination: newPagination });
  };

  const handlePageAction = useCallback(
    (action: PickingPageAction, date?: Dates) => {
      switch (action) {
        case PickingPageAction.CHANGE_DATE:
          setActiveDate(date!);
          break;
      }
    },
    []
  );

  const onPrintPickTicketsClicked = useCallback(async () => {
    const chosenPreset = reportPresets.find((p) => p.defaultFlag === true);
    const params = chosenPreset?.parameters || {};
    try {
      await printPickTicketsReport(selectedItems, params);
    } catch (error) {
      console.error('Failed to print pick tickets report:', error);
      showNotification(
        'Failed to print pick tickets. Please try again or contact support if the issue persists.',
        { variant: 'error' }
      );
    }
  }, [selectedItems, reportPresets]);

  const pickViewData = {
    searchResult,
    activePickId,
    reportPresets,
    activeUser,
    flags,
    users,
    locations,
    customers,
    orderPriorities,
    activeDate,
    searchItem,
    searchTags,
  };

  const pickUiState = {
    isVoiding,
    isGrouping,
    isLoadingSearchResult,
    activeDate,
  };

  const pickSelection = {
    selectedItems,
    setSelectedItems,
  };

  const pickHandlers = {
    fetchSearchResult,
    handlePickClicked,
    handleDetailsCardClosed,
    handlePaginationChange,
    handlePageAction,
    executePickingBulkAction,
    onPrintPickTicketsClicked,
  };

  return (
    <>
      <ModuleNavigation />
      <PickingPageView
        data={pickViewData}
        uiState={pickUiState}
        lookups={{}}
        selection={pickSelection}
        handlers={pickHandlers}
      />
    </>
  );
};

PickingPage.route = Routes.PickingPage;

export default withSearchResults<DisplayPick>(PickingPage, {
  url: '/warehouse-mgmt/v1/search-picks?showStockStatus=true',
  expand: '',
  dataAdapter: transformDisplayPick,
  columns: PICKING_COLUMNS([], Dates.DateScheduled),
  getSearches,
  fetchSearch,
  initialPagination,
  quickSearchColumns: [
    'number',
    'customer.name',
    'parentLocation.name',
    'orderPriority.name',
    'status',
  ],
  rehydrationThunks: [
    fetchUsers,
    fetchUsersV2,
    fetchCustomers,
    fetchLocations,
    fetchUoms,
    fetchTags,
    fetchOrderPriorities,
    fetchSettingsPicking,
  ],
});
